/**
 * Simplified Unified AI Service for AdapTeach
 * Uses direct AI API calls without Supabase Edge Functions
 */

import {
  LessonPlanRequest,
  LessonPlanResponse,
  AssessmentRequest,
  AssessmentResponse,
  getSumoPodAIService,
} from "./sumpodAIService";

export type AIProvider = "sumpod-openai";

export interface UnifiedAIConfig {
  primaryProvider: AIProvider;
  enableFallback: boolean;
  enableABTesting: boolean;
  enablePerformanceMonitoring: boolean;
}

export interface GenerationOptions {
  useStreaming?: boolean;
  onStreamChunk?: (content: string, chunkIndex: number) => void;
  onStreamComplete?: (
    fullContent: string,
    metadata?: Record<string, unknown>
  ) => void;
  onStreamError?: (error: string) => void;
  onStreamInit?: (title: string, provider: string) => void;
}

/**
 * Simplified Unified AI Service Class
 */
export class UnifiedAIService {
  private config: UnifiedAIConfig;

  constructor(config?: Partial<UnifiedAIConfig>) {
    this.config = {
      primaryProvider: "sumpod-openai",
      enableFallback: import.meta.env.VITE_ENABLE_FALLBACK !== "false",
      enableABTesting: import.meta.env.VITE_AB_TEST_ENABLED === "true",
      enablePerformanceMonitoring:
        import.meta.env.VITE_PERFORMANCE_MONITORING !== "false",
      ...config,
    };
  }

  /**
   * Generate lesson plan with SumoPod AI service
   */
  async generateLessonPlan(
    formData: LessonPlanRequest
  ): Promise<LessonPlanResponse> {
    try {
      const sumoPodAIService = getSumoPodAIService();
      return await sumoPodAIService.generateLessonPlan(formData);
    } catch (error) {
      console.error(
        `💥 Primary provider ${this.config.primaryProvider} failed:`,
        error
      );

      // Try fallback if enabled
      if (this.config.enableFallback) {
        console.warn(`🔄 Attempting fallback...`);

        try {
          // Use fallback template from SumoPod AI service
          const sumoPodAIService = getSumoPodAIService();
          return await sumoPodAIService.generateLessonPlan(formData);
        } catch (fallbackError) {
          console.error(`💥 Fallback also failed:`, fallbackError);
        }
      }

      throw error;
    }
  }

  /**
   * Generate assessment with SumoPod AI service
   */
  async generateAssessment(
    formData: AssessmentRequest
  ): Promise<AssessmentResponse> {
    try {
      const sumoPodAIService = getSumoPodAIService();
      return await sumoPodAIService.generateAssessment(formData);
    } catch (error) {
      console.error(
        `💥 Primary provider ${this.config.primaryProvider} failed:`,
        error
      );

      // Try fallback if enabled
      if (this.config.enableFallback) {
        console.warn(`🔄 Attempting fallback...`);

        try {
          // Use fallback template from SumoPod AI service
          const sumoPodAIService = getSumoPodAIService();
          return await sumoPodAIService.generateAssessment(formData);
        } catch (fallbackError) {
          console.error(`💥 Fallback also failed:`, fallbackError);
        }
      }

      throw error;
    }
  }

  /**
   * Get current configuration
   */
  getConfig(): UnifiedAIConfig {
    return { ...this.config };
  }

  /**
   * Get provider status
   */
  getProviderStatus(): {
    provider: string;
    model: string;
    hasApiKey: boolean;
    streamingEnabled: boolean;
    config: UnifiedAIConfig;
  } {
    const sumoPodAIService = getSumoPodAIService();
    const serviceStatus = sumoPodAIService.getServiceStatus();

    return {
      provider: serviceStatus.provider,
      model: serviceStatus.model,
      hasApiKey: serviceStatus.hasApiKey,
      streamingEnabled: serviceStatus.hasApiKey, // Enable streaming if API key is available
      config: this.config,
    };
  }

  /**
   * Switch provider (for future extensibility)
   */
  switchProvider(newProvider: AIProvider): void {
    this.config.primaryProvider = newProvider;
  }
}

/**
 * Create a singleton instance of the Unified AI service
 */
let unifiedAIServiceInstance: UnifiedAIService | null = null;

export const getUnifiedAIService = (): UnifiedAIService => {
  if (!unifiedAIServiceInstance) {
    unifiedAIServiceInstance = new UnifiedAIService();
  }
  return unifiedAIServiceInstance;
};

/**
 * Reset the singleton instance (useful for testing)
 */
export const resetUnifiedAIService = (): void => {
  unifiedAIServiceInstance = null;
};

/**
 * Convenience functions for direct usage
 */
export const generateLessonPlanUnified = async (
  formData: LessonPlanRequest
): Promise<LessonPlanResponse> => {
  const service = getUnifiedAIService();
  return await service.generateLessonPlan(formData);
};

export const generateAssessmentUnified = async (
  formData: AssessmentRequest
): Promise<AssessmentResponse> => {
  const service = getUnifiedAIService();
  return await service.generateAssessment(formData);
};
